import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Users, CalendarDays, Sparkles, Coins, Cpu, Compass, Activity, Tag,
  UserCircle, ImagePlus, Radio,
} from 'lucide-react';
import api from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import {
  FunnelPanel, BackfillPanel, HealthTriagePanel, AudiencesPanel,
} from './AdminAnalyticsBundle62Sections';
import AdminEventSurveyAnalyticsPanel from './AdminEventSurveyAnalyticsPanel';
import ConnectionFunnelTile from './ConnectionFunnelTile';

// Batch 4 P0: Consolidate Registrations analytics + Pixel & Traffic analytics
// into a single tabbed dashboard. Old /admin/pixels/analytics route now
// redirects here with ?tab=pixel.
const AdminPixelAnalyticsPage = React.lazy(() => import('./AdminPixelAnalyticsPage'));

const AdminAnalyticsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const initialTab = (() => {
    const t = new URLSearchParams(location.search).get('tab');
    if (['pixel', 'funnel', 'backfill', 'health', 'audiences', 'survey'].includes(t)) return t;
    return 'registrations';
  })();
  const [tab, setTab] = useState(initialTab);
  const onTabChange = (next) => {
    setTab(next);
    const q = new URLSearchParams(location.search);
    if (next !== 'registrations') q.set('tab', next); else q.delete('tab');
    navigate({ pathname: location.pathname, search: q.toString() ? `?${q.toString()}` : '' }, { replace: true });
  };

  return (
    <div className="p-8 space-y-4 max-w-6xl" data-testid="admin-analytics-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-primary-900">Analytics</h1>
        <p className="text-neutral-600 text-sm">Live from the database. No estimates unless labeled.</p>
        <p
          className="text-[11px] text-neutral-500 mt-1"
          data-testid="analytics-admin-excluded-footnote"
        >
          Admin sessions and /admin/* routes are excluded by default.
        </p>
      </div>
      {isSuperAdmin ? <ConnectionFunnelTile /> : null}
      <Tabs value={tab} onValueChange={onTabChange} className="w-full">
        <TabsList data-testid="admin-analytics-tablist">
          <TabsTrigger value="registrations" data-testid="admin-analytics-tab-registrations">Registrations</TabsTrigger>
          <TabsTrigger value="pixel" data-testid="admin-analytics-tab-pixel">Pixel &amp; Traffic</TabsTrigger>
          <TabsTrigger value="survey" data-testid="admin-analytics-tab-survey">Survey</TabsTrigger>
          {isSuperAdmin && (
            <>
              <TabsTrigger value="funnel" data-testid="admin-analytics-tab-funnel">Funnel</TabsTrigger>
              <TabsTrigger value="backfill" data-testid="admin-analytics-tab-backfill">CAPI Backfill</TabsTrigger>
              <TabsTrigger value="audiences" data-testid="admin-analytics-tab-audiences">Audiences</TabsTrigger>
              <TabsTrigger value="health" data-testid="admin-analytics-tab-health">Health Triage</TabsTrigger>
            </>
          )}
        </TabsList>
        <TabsContent value="registrations">
          <RegistrationsPanel />
          <RevenueTileGroup />
        </TabsContent>
        <TabsContent value="pixel">
          <Suspense fallback={<div className="p-10 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" /></div>}>
            <AdminPixelAnalyticsPage embedded />
          </Suspense>
        </TabsContent>
        <TabsContent value="survey">
          <AdminEventSurveyAnalyticsPanel />
        </TabsContent>
        {isSuperAdmin && (
          <>
            <TabsContent value="funnel"><FunnelPanel /></TabsContent>
            <TabsContent value="backfill"><BackfillPanel /></TabsContent>
            <TabsContent value="audiences"><AudiencesPanel /></TabsContent>
            <TabsContent value="health"><HealthTriagePanel /></TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
};

const RegistrationsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/admin/analytics/overview');
        setData(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Could not load analytics');
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return <div className="p-10 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" /></div>;
  }
  if (err) {
    return <div className="p-10 text-center text-accent-700">{err}</div>;
  }

  // Pick a "primary" event to drill into for aggregate cards - the event
  // with the most RSVPs, else the first event.
  const primaryEvent = (data.per_event || []).slice().sort((a, b) => (b.rsvp_count || 0) - (a.rsvp_count || 0))[0];
  const regsLink = (params = {}) => {
    if (!primaryEvent) return '#';
    const qs = new URLSearchParams(params).toString();
    return `/admin/events/${primaryEvent.event_id}/registrations${qs ? `?${qs}` : ''}`;
  };

  return (
    <div className="space-y-6 pt-2" data-testid="admin-analytics-registrations-panel">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          {data.test_excluded_count > 0 && (
            <p
              className="text-[11px] text-neutral-500"
              data-testid="analytics-test-excluded-footnote"
            >
              Excludes {data.test_excluded_count} test registration{data.test_excluded_count === 1 ? '' : 's'}.
            </p>
          )}
        </div>
        <div className="text-[11px] text-neutral-400">Generated {data.generated_at ? new Date(data.generated_at).toLocaleTimeString() : ''}</div>
      </div>

      {/* Totals */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Users} label="Total RSVPs" value={data.totals.rsvps} testid="stat-total-rsvps" to={regsLink()} />
        <StatCard icon={Sparkles} label="Verified" value={data.totals.verified} testid="stat-verified" to={regsLink({ status_filter: 'submitted' })} />
        <StatCard icon={CalendarDays} label="Confirmed" value={data.totals.confirmed} testid="stat-confirmed" to={regsLink({ status_filter: 'confirmed' })} />
        <StatCard icon={Compass} label="Events" value={data.totals.events} testid="stat-events" to="/admin/events" />
      </div>

      {/* Per-event breakdown */}
      <Card className="border shadow-soft">
        <CardContent className="p-6">
          <div className="text-[11px] uppercase tracking-widest text-secondary-700 mb-3">Per event</div>
          {data.per_event.length === 0 ? (
            <div className="text-sm text-neutral-500 py-6 text-center">No events yet.</div>
          ) : (
            <div className="space-y-2">
              {data.per_event.map((e) => (
                <Link
                  key={e.event_id}
                  to={`/admin/events/${e.event_id}/registrations`}
                  className="flex items-center gap-3 p-3 rounded-lg border border-neutral-100 hover:border-secondary-300 hover:shadow-md bg-white transition-all cursor-pointer"
                  data-testid={`per-event-${e.event_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-primary-900 truncate">{e.title}</div>
                    <div className="text-[10px] font-mono text-neutral-400">/{e.slug}</div>
                  </div>
                  <div className="text-right w-20">
                    <div className="font-mono text-primary-900">{e.rsvp_count}</div>
                    <div className="text-[9px] uppercase tracking-widest text-neutral-500">RSVPs</div>
                  </div>
                  <div className="text-right w-20">
                    <div className="font-mono text-primary-900">{e.capacity || '-'}</div>
                    <div className="text-[9px] uppercase tracking-widest text-neutral-500">Cap</div>
                  </div>
                  <div className="w-32">
                    <div className="text-xs text-neutral-500 mb-0.5">{e.fill_pct}% full</div>
                    <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-secondary-500 to-primary-700" style={{ width: `${Math.min(100, e.fill_pct)}%` }} />
                    </div>
                  </div>
                  <Badge variant="outline" className="capitalize text-[10px]">{e.state || 'draft'}</Badge>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        {/* By intent */}
        <Card className="border shadow-soft">
          <CardContent className="p-6">
            <div className="text-[11px] uppercase tracking-widest text-secondary-700 mb-3">By intent</div>
            {data.by_intent.length === 0 ? (
              <div className="text-sm text-neutral-500">No intent selections yet.</div>
            ) : (
              <BarList
                items={data.by_intent.map((x) => ({ label: x.intent.replace(/_/g, ' '), count: x.count, _raw: x.intent }))}
                testid="bars-intents"
                hrefFor={(it) => regsLink({ intent: it._raw })}
              />
            )}
          </CardContent>
        </Card>

        {/* AI usage today */}
        <Card className="border shadow-soft">
          <CardContent className="p-6">
            <div className="text-[11px] uppercase tracking-widest text-secondary-700 mb-3 flex items-center gap-1.5"><Cpu className="h-3.5 w-3.5" /> Dost AI - today</div>
            <div className="grid grid-cols-2 gap-3">
              <SmallStat label="Polishes" value={data.ai_today.polish_about_me} />
              <SmallStat label="Drafts" value={data.ai_today.admin_draft_content} />
              <SmallStat label="Tokens" value={data.ai_today.total_tokens.toLocaleString()} />
              <SmallStat label="Cost" value={`$${(data.ai_today.estimated_cost_cents / 100).toFixed(4)}`} sublabel="approx" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Top interests */}
        <Card className="border shadow-soft">
          <CardContent className="p-6">
            <div className="text-[11px] uppercase tracking-widest text-secondary-700 mb-3 flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" /> Top interests</div>
            {data.top_interests.length === 0 ? (
              <div className="text-sm text-neutral-500">No selections yet.</div>
            ) : (
              <BarList
                items={data.top_interests}
                testid="bars-interests"
                hrefFor={(it) => regsLink({ interest_label: it.label })}
              />
            )}
          </CardContent>
        </Card>

        {/* Top activities */}
        <Card className="border shadow-soft">
          <CardContent className="p-6">
            <div className="text-[11px] uppercase tracking-widest text-secondary-700 mb-3 flex items-center gap-1.5"><Activity className="h-3.5 w-3.5" /> Top activities</div>
            {data.top_activities.length === 0 ? (
              <div className="text-sm text-neutral-500">No selections yet.</div>
            ) : (
              <BarList
                items={data.top_activities}
                testid="bars-activities"
                hrefFor={(it) => regsLink({ activity_label: it.label })}
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Batch 4: Registration demographics + traffic sources */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card className="border shadow-soft" data-testid="analytics-gender-card">
          <CardContent className="p-6">
            <div className="text-[11px] uppercase tracking-widest text-secondary-700 mb-3 flex items-center gap-1.5">
              <UserCircle className="h-3.5 w-3.5" /> Gender distribution
            </div>
            <GenderBar block={data.by_gender} testid="gender-bar-global" />
          </CardContent>
        </Card>
        <Card className="border shadow-soft" data-testid="analytics-photo-card">
          <CardContent className="p-6">
            <div className="text-[11px] uppercase tracking-widest text-secondary-700 mb-3 flex items-center gap-1.5">
              <ImagePlus className="h-3.5 w-3.5" /> Profile completion
            </div>
            <PhotoCompletionBlock block={data.photo_completion} testid="photo-completion-global" />
          </CardContent>
        </Card>
        <Card className="border shadow-soft" data-testid="analytics-sources-card">
          <CardContent className="p-6">
            <div className="text-[11px] uppercase tracking-widest text-secondary-700 mb-3 flex items-center gap-1.5">
              <Radio className="h-3.5 w-3.5" /> Traffic sources
            </div>
            {(!data.by_source || data.by_source.length === 0) ? (
              <div className="text-sm text-neutral-500">No source data captured yet.</div>
            ) : (
              <BarList
                items={data.by_source.map((s) => ({ label: s.label, count: s.count, _raw: s.source }))}
                testid="bars-sources"
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* RSVPs by day */}
      <Card className="border shadow-soft">
        <CardContent className="p-6">
          <div className="text-[11px] uppercase tracking-widest text-secondary-700 mb-3">RSVPs - last 30 days</div>
          <DayBars data={data.rsvps_by_day} />
        </CardContent>
      </Card>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, testid, to }) => {
  const inner = (
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-secondary-700">{label}</div>
          <div className="font-display text-3xl font-bold text-primary-900 mt-1">{value}</div>
        </div>
        <div className="w-9 h-9 rounded-lg bg-primary-50 text-primary-800 flex items-center justify-center">
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </CardContent>
  );
  const card = <Card className={`border shadow-soft ${to ? 'hover:border-secondary-400 hover:shadow-md transition-all cursor-pointer' : ''}`} data-testid={testid}>{inner}</Card>;
  return to ? <Link to={to}>{card}</Link> : card;
};

const SmallStat = ({ label, value, sublabel }) => (
  <div className="p-3 rounded-lg bg-neutral-50/60 border border-neutral-100">
    <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
    <div className="text-lg font-semibold text-primary-900">{value}</div>
    {sublabel && <div className="text-[9px] text-neutral-400">{sublabel}</div>}
  </div>
);

const BarList = ({ items, testid, hrefFor }) => {
  const max = useMemo(() => Math.max(1, ...items.map((x) => x.count)), [items]);
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {items.map((x, i) => {
        const row = (
          <div className="flex items-center gap-3 py-0.5 group">
            <div className={`w-32 text-sm text-primary-900 truncate ${hrefFor ? 'group-hover:text-secondary-800' : ''}`}>{x.label}</div>
            <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-secondary-400 to-primary-700" style={{ width: `${(x.count / max) * 100}%` }} />
            </div>
            <div className="w-10 text-right text-xs font-mono text-neutral-600">{x.count}</div>
          </div>
        );
        return hrefFor ? (
          <Link key={i} to={hrefFor(x)} className="block rounded cursor-pointer hover:bg-neutral-50/60 -mx-1 px-1">{row}</Link>
        ) : (
          <div key={i}>{row}</div>
        );
      })}
    </div>
  );
};

const DayBars = ({ data }) => {
  const max = Math.max(1, ...data.map((x) => x.count));
  return (
    <div className="flex items-end gap-1 h-32">
      {data.map((d) => (
        <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.count}`}>
          <div
            className="w-full bg-gradient-to-t from-secondary-300 to-primary-700 rounded-t"
            style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count > 0 ? '3px' : '1px', opacity: d.count > 0 ? 1 : 0.15 }}
          />
        </div>
      ))}
    </div>
  );
};

// Batch 4: Gender M/F two-segment bar. Brand tokens only (male=primary,
// female=secondary; mirrors GenderPill decision from Task 1c).
const GenderBar = ({ block, testid }) => {
  const male = block?.male || { count: 0, percentage: 0 };
  const female = block?.female || { count: 0, percentage: 0 };
  const total = male.count + female.count;
  if (total === 0) {
    return <div className="text-sm text-neutral-500">No gender data captured yet.</div>;
  }
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex h-3 rounded-full overflow-hidden bg-neutral-100">
        <div
          className="bg-primary-700"
          style={{ width: `${male.percentage}%` }}
          data-testid={`${testid}-male-seg`}
          title={`Male ${male.count} (${male.percentage}%)`}
        />
        <div
          className="bg-secondary-500"
          style={{ width: `${female.percentage}%` }}
          data-testid={`${testid}-female-seg`}
          title={`Female ${female.count} (${female.percentage}%)`}
        />
      </div>
      <div className="flex items-center justify-between text-xs" data-testid={`${testid}-legend`}>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-primary-700 shrink-0" />
          <span className="text-primary-900 font-semibold">Male</span>
          <span className="text-neutral-500">{male.count} · {male.percentage}%</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-secondary-500 shrink-0" />
          <span className="text-primary-900 font-semibold">Female</span>
          <span className="text-neutral-500">{female.count} · {female.percentage}%</span>
        </div>
      </div>
    </div>
  );
};

const PhotoCompletionBlock = ({ block, testid }) => {
  if (!block || block.total_count === 0) {
    return <div className="text-sm text-neutral-500">No registrations yet.</div>;
  }
  const male = block.by_gender?.male || { with_photos_count: 0, total_count: 0, pct_of_male: 0 };
  const female = block.by_gender?.female || { with_photos_count: 0, total_count: 0, pct_of_female: 0 };
  return (
    <div className="space-y-3" data-testid={testid}>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500">Overall with profile picture</div>
        <div className="font-display text-2xl font-bold text-primary-900" data-testid={`${testid}-overall`}>
          {block.with_photos_percentage}%
        </div>
        <div className="text-[11px] text-neutral-500">
          {block.with_photos_count} of {block.total_count} RSVPs
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-neutral-100">
        <div data-testid={`${testid}-female`}>
          <div className="text-[10px] uppercase tracking-widest text-secondary-700">Female with picture</div>
          <div className="text-primary-900 font-semibold">{female.pct_of_female}%</div>
          <div className="text-[11px] text-neutral-500">{female.with_photos_count} of {female.total_count}</div>
        </div>
        <div data-testid={`${testid}-male`}>
          <div className="text-[10px] uppercase tracking-widest text-primary-700">Male with picture</div>
          <div className="text-primary-900 font-semibold">{male.pct_of_male}%</div>
          <div className="text-[11px] text-neutral-500">{male.with_photos_count} of {male.total_count}</div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalyticsPage;

// ================================================================ //
// Bundle 127 - Revenue tile group.
// Powered by GET /admin/payments/analytics/summary (Bundle 125).
// Small-bucket privacy: gender counts < 5 rendered as "<5".
// ================================================================ //
const _redact = (v) => (typeof v === 'number' && v > 0 && v < 5 ? '<5' : v);

const RevenueTileGroup = () => {
  const [data, setData] = React.useState(null);
  const [tierGender, setTierGender] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');

  React.useEffect(() => {
    (async () => {
      try {
        const [summary, tg] = await Promise.all([
          api.get('/admin/payments/analytics/summary'),
          api.get('/admin/payments/by-event').then(async (r) => {
            const first = (r.data.events || [])[0];
            if (!first) return { data: { gender_by_tier: {} } };
            return api.get(
              `/admin/payments/by-event/${first.event_id}/detail`,
            );
          }).catch(() => ({ data: { gender_by_tier: {} } })),
        ]);
        setData(summary.data);
        setTierGender(tg.data?.gender_by_tier || {});
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Could not load revenue.');
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="mt-6" data-testid="revenue-tiles-loading">
        <div className="animate-pulse text-neutral-400 text-sm">Loading revenue.</div>
      </div>
    );
  }
  if (err) return (
    <div className="mt-6 text-accent-700 text-sm" data-testid="revenue-tiles-err">{err}</div>
  );
  if (!data) return null;
  const money = (c) => c == null ? '-' : `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const byTier = data.revenue_by_tier || [];
  const byMonth = data.revenue_by_month || [];
  const maxTier = Math.max(1, ...byTier.map((t) => t.revenue_cents));
  const maxMonth = Math.max(1, ...byMonth.map((m) => m.revenue_cents));

  return (
    <div className="mt-8 space-y-4" data-testid="revenue-tiles-group">
      <div className="flex items-baseline justify-between">
        <h2 className="text-lg font-semibold text-primary-900">Revenue</h2>
        <div className="text-[10px] text-neutral-400" data-testid="revenue-last-computed">
          Computed {data.last_computed_at ? new Date(data.last_computed_at).toLocaleTimeString() : ''}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card><CardContent className="p-4" data-testid="rev-tile-net">
          <div className="text-[10px] uppercase tracking-widest text-secondary-700">Net after refunds</div>
          <div className="font-display text-2xl font-bold text-primary-900">
            {money(data.grand_total_net_after_refunds_cents)}
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-4" data-testid="rev-tile-avg">
          <div className="text-[10px] uppercase tracking-widest text-secondary-700">Avg ticket price</div>
          <div className="font-display text-2xl font-bold text-primary-900">
            {money(data.grand_avg_ticket_price_cents)}
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-4" data-testid="rev-tile-tickets">
          <div className="text-[10px] uppercase tracking-widest text-secondary-700">Tickets sold</div>
          <div className="font-display text-2xl font-bold text-primary-900">
            {data.grand_ticket_count || 0}
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-4" data-testid="rev-tile-gross">
          <div className="text-[10px] uppercase tracking-widest text-secondary-700">Gross</div>
          <div className="font-display text-2xl font-bold text-primary-900">
            {money(data.grand_total_gross_cents)}
          </div>
        </CardContent></Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card><CardContent className="p-4" data-testid="rev-tile-by-tier">
          <div className="text-[10px] uppercase tracking-widest text-secondary-700 mb-2">
            Revenue by tier
          </div>
          {byTier.length === 0 ? (
            <div className="text-neutral-500 text-sm">No tier data.</div>
          ) : (
            <div className="space-y-2">
              {byTier.map((t) => (
                <div key={t.tier_name} data-testid={`rev-by-tier-${t.tier_name}`}>
                  <div className="flex justify-between text-xs">
                    <span className="text-primary-900 font-semibold">{t.tier_name}</span>
                    <span className="text-neutral-500">{money(t.revenue_cents)}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden mt-1">
                    <div className="h-full bg-gradient-to-r from-secondary-500 to-primary-700"
                          style={{ width: `${(t.revenue_cents / maxTier) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent></Card>

        <Card><CardContent className="p-4" data-testid="rev-tile-gender-by-tier">
          <div className="text-[10px] uppercase tracking-widest text-secondary-700 mb-2">
            Gender by tier (top event)
          </div>
          {(!tierGender || Object.keys(tierGender).length === 0) ? (
            <div className="text-neutral-500 text-sm">No data.</div>
          ) : (
            <div className="space-y-2">
              {Object.entries(tierGender).map(([tier, buckets]) => {
                const numeric = Object.entries(buckets).filter(([, v]) => typeof v === 'number');
                const total = numeric.reduce((a, [, v]) => a + v, 0) || 1;
                return (
                  <div key={tier} data-testid={`rev-gender-by-tier-${tier}`}>
                    <div className="text-xs text-primary-900 font-semibold">{tier}</div>
                    <div className="flex h-2 rounded-full overflow-hidden bg-neutral-100">
                      {numeric.map(([g, v], i) => (
                        <div key={g}
                              className={[
                                'bg-primary-700', 'bg-secondary-500',
                                'bg-accent-500', 'bg-neutral-400',
                              ][i % 4]}
                              style={{ width: `${(v / total) * 100}%` }}
                              title={`${g}: ${v}`} />
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-x-2 text-[10px] text-neutral-500 mt-0.5">
                      {Object.entries(buckets).map(([g, v]) => (
                        <span key={g}>{g}: {_redact(v)}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent></Card>
      </div>

      <Card><CardContent className="p-4" data-testid="rev-tile-monthly">
        <div className="text-[10px] uppercase tracking-widest text-secondary-700 mb-2">
          Revenue last 12 months
        </div>
        {byMonth.length === 0 ? (
          <div className="text-neutral-500 text-sm">No monthly data.</div>
        ) : (
          <div className="flex items-end gap-1 h-24">
            {byMonth.map((m) => (
              <div key={m.month} className="flex-1 flex flex-col items-center" title={`${m.month}: ${money(m.revenue_cents)}`}>
                <div className="w-full bg-gradient-to-t from-secondary-300 to-primary-700 rounded-t"
                      style={{ height: `${(m.revenue_cents / maxMonth) * 100}%`, minHeight: m.revenue_cents > 0 ? 3 : 1 }} />
                <div className="text-[9px] text-neutral-500 mt-1">{m.month.slice(5)}</div>
              </div>
            ))}
          </div>
        )}
      </CardContent></Card>
    </div>
  );
};
